import {SxProps, Theme} from '@mui/material';
import {alpha} from '@mui/material/styles';

export interface FlatTreeRow {
    level: number;
    parentPath: number[];
}

/** MUI spacing units per tree level (matches sidebar / DeptList convention). */
export const TREE_LEVEL_INDENT_SPACING = 2;

/** Base leading padding in MUI spacing units. */
export const TREE_LEVEL_BASE_SPACING = 1;

/** Width matching MUI IconButton size="small" - used as spacer for leaf rows. */
export const TREE_EXPAND_BUTTON_WIDTH = 34;

export function treeLeadingPadding(level: number): number {
    return TREE_LEVEL_BASE_SPACING + level * TREE_LEVEL_INDENT_SPACING;
}

export function treeLeadingCellSx(level: number): SxProps<Theme> {
    return {
        pl: theme => theme.spacing(treeLeadingPadding(level)),
        whiteSpace: 'nowrap',
    };
}

/** Same hue (secondary), deeper tint per tree level. Cap keeps text readable. */
export function treeLevelRowBg(level: number, theme: Theme): string {
    const opacity = Math.min(0.04 + level * 0.05, 0.24);
    return alpha(theme.palette.secondary.main, opacity);
}

/**
 * Open parent ids so rows through maxVisibleLevel (0-based) are visible.
 * e.g. maxVisibleLevel=1 shows level-0 and level-1 (opens all level-0 parents).
 */
export function defaultOpenIdsForLevels<T extends { children?: T[] }>(
    nodes: T[] | undefined,
    getId: (node: T) => number,
    maxVisibleLevel: number,
): number[] {
    const ids: number[] = [];
    const walk = (list: T[] | undefined, level: number) => {
        if (!list || level >= maxVisibleLevel) return;
        list.forEach((node) => {
            if (node.children && node.children.length > 0) {
                ids.push(getId(node));
                walk(node.children, level + 1);
            }
        });
    };
    walk(nodes, 0);
    return ids;
}

export function flattenTree<T extends { children?: T[] }>(
    nodes: T[] | undefined,
    getId: (node: T) => number,
    level = 0,
    parentPath: number[] = [],
): (T & FlatTreeRow)[] {
    let rows: (T & FlatTreeRow)[] = [];
    nodes?.forEach(node => {
        rows.push({...node, level, parentPath});
        if (node.children && node.children.length > 0) {
            rows = rows.concat(
                flattenTree(node.children, getId, level + 1, [...parentPath, getId(node)]),
            );
        }
    });
    return rows;
}

export function getVisibleTreeRows<T extends FlatTreeRow>(
    rows: T[],
    openIds: number[],
): T[] {
    const idSet = new Set(openIds);
    const isParentClosed = (row: T) =>
        row.parentPath?.some(pid => !idSet.has(pid));
    return rows.filter(row => row.level === 0 || !isParentClosed(row));
}
